import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { objectNotNullV2 } from '@/Utils/FnUtils';
import { apiClient } from '@/_config/lib/axios/api-client';
import { handleResponseThrowError } from '@/_utils/services';
import type { GetMissionsListParams, Mission } from '../types';
import type { BaseRequestResponse } from '@/_types/services';

export const GET_MISSIONS = 'GET_MISSIONS';

const transformParams = (params: GetMissionsListParams) => {
  const { cityId, missionName, status, startDate, endDate, page, limit } =
    params || {};

  const newPage = page || 1;
  const newLimit = limit || DEFAULT_LIMIT_REQUEST;
  const offset = (newPage - 1) * newLimit;

  return objectNotNullV2({
    cityId,
    missionName,
    status,
    startDate: startDate ? new Date(startDate).toISOString() : undefined,
    endDate: endDate ? new Date(endDate).toISOString() : undefined,
    limit: newLimit,
    offset,
  });
};

const getMissions = (params: GetMissionsListParams) => {
  const objParams = transformParams(params);

  return apiClient
    .get<BaseRequestResponse<Mission[]>>('/missions/by-manager', {
      params: objParams,
    })
    .then(response => response.data)
    .catch(handleResponseThrowError());
};

type GetMissionsData = Awaited<ReturnType<typeof getMissions>>;

export const useGetMissions = (
  params: GetMissionsListParams,
  options: Omit<
    UseQueryOptions<
      GetMissionsData,
      unknown,
      GetMissionsData,
      (string | GetMissionsListParams)[]
    >,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery({
    queryKey: [GET_MISSIONS, params],
    queryFn: () => getMissions(params),
    ...options,
  });
};
